'use client'

import { useEffect, useMemo, useRef } from 'react'

interface SparklesProps {
  active: boolean
  isHovered: boolean
}

export const Sparkles = ({ active, isHovered }: SparklesProps) => {
  const particles = useMemo(() => Array.from({ length: 35 }), []) // More numerous particles
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!active || !isHovered || !containerRef.current) return

    const container = containerRef.current
    const particleElements = container.children

    // Setup particles
    Array.from(particleElements).forEach((particle) => {
      const element = particle as HTMLDivElement
      // Random position around the container
      const positionParticle = () => {
        const angle = Math.random() * Math.PI * 2
        // Create multiple layers of particles
        const layerRoll = Math.random()
        let distance

        if (layerRoll < 0.3) {
          // Inner layer - close to the card
          distance = 20 + Math.random() * 60
        } else if (layerRoll < 0.7) {
          // Middle layer
          distance = 80 + Math.random() * 60
        } else {
          // Outer layer
          distance = 140 + Math.random() * 70
        }

        const x = Math.cos(angle) * distance
        const y = Math.sin(angle) * distance

        // Smaller sizes but more numerous
        const sizeRoll = Math.random()
        let size

        if (sizeRoll < 0.7) {
          // Small size
          size = 2 + sizeRoll * 2
        } else if (sizeRoll < 0.9) {
          // Medium sparkles
          size = 4 + sizeRoll * 3
        } else {
          // Few larger sparkles
          size = 7 + sizeRoll * 2
        }

        // Even faster animation for some particles
        const durationRoll = Math.random()
        const duration = durationRoll < 0.3 ? 0.8 + durationRoll : 1 + durationRoll

        // Random delay - shorter for more frequent sparkles
        const delay = Math.random() * 0.8

        // Higher opacity for more visibility
        const maxOpacity = 0.7 + Math.random() * 0.3

        // More vibrant colors
        const colorRoll = Math.random()
        let color

        if (colorRoll < 0.4) {
          color = '#FFFFFF' // White
        } else if (colorRoll < 0.6) {
          color = 'rgba(170, 200, 255, 1)' // Light blue
        } else if (colorRoll < 0.8) {
          color = 'rgba(200, 180, 255, 1)' // Light purple
        } else {
          color = 'rgba(255, 230, 180, 1)' // Light gold
        }

        // Set styles
        element.style.width = `${size}px`
        element.style.height = `${size}px`
        element.style.left = `calc(50% + ${x}px)`
        element.style.top = `calc(50% + ${y}px)`
        element.style.animationDuration = `${duration}s`
        element.style.animationDelay = `${delay}s`
        element.style.setProperty('--max-opacity', maxOpacity.toString())
        element.style.backgroundColor = color

        // Enhanced glow effect - smaller for smaller particles
        element.style.boxShadow = `0 0 ${size * 1.2}px ${color.replace('1)', '0.8)')}`

        // Add shape variation - some particles as stars
        const shapeRoll = Math.random()
        if (shapeRoll > 0.8) {
          // Fewer stars
          // Create star-like shape
          element.style.borderRadius = '0'
          element.style.clipPath =
            'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
        } else {
          element.style.borderRadius = '50%'
        }

        // Add additional animation variants
        const animVariant = Math.floor(Math.random() * 3) + 1
        element.style.animationName = `sparkleAnimation${animVariant}`
      }

      positionParticle()

      // Reset position when animation ends
      const resetPosition = () => {
        positionParticle()
      }

      element.addEventListener('animationiteration', resetPosition)

      return () => {
        element.removeEventListener('animationiteration', resetPosition)
      }
    })
  }, [active, isHovered, particles])

  if (!active || !isHovered) return null

  return (
    <>
      <style jsx global>{`
        @keyframes sparkleAnimation1 {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          10% {
            opacity: var(--max-opacity, 0.9);
          }
          100% {
            transform: scale(1.8);
            opacity: 0;
          }
        }

        @keyframes sparkleAnimation2 {
          0% {
            transform: scale(0.2);
            opacity: 0;
          }
          15% {
            transform: scale(1);
            opacity: var(--max-opacity, 0.9);
          }
          30% {
            transform: scale(0.8);
            opacity: calc(var(--max-opacity, 0.9) * 0.7);
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }

        @keyframes sparkleAnimation3 {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          10% {
            transform: scale(0.8);
            opacity: var(--max-opacity, 0.9);
          }
          20% {
            transform: scale(1.1);
            opacity: calc(var(--max-opacity, 0.9) * 0.8);
          }
          100% {
            transform: scale(1.6);
            opacity: 0;
          }
        }
      `}</style>

      <div
        ref={containerRef}
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ zIndex: 20 }}
      >
        {particles.map((_, i) => (
          <div
            key={i}
            className="absolute opacity-0 will-change-transform"
            style={{
              animation: `sparkleAnimation1 2s infinite ease-out`,
            }}
          />
        ))}
      </div>
    </>
  )
}
